import { apiErrorCode } from "../../lib/apiErrors";
import { TAG_MAX_LENGTH } from "../hqCustomerRules";
import { ACTION_CONFIG_FIELDS, RULE_NAME_MAX_LENGTH } from "./actionConfigRules";
import { actionLabel } from "./automationCodes";

// The limit each config field's rule sentence names (`hauto.cfg.rule.<field>`).
const RULE_MAX = Object.fromEntries(
  Object.values(ACTION_CONFIG_FIELDS).flat().filter((f) => f.max).map((f) => [f.name, f.max]),
);
RULE_MAX.tag = TAG_MAX_LENGTH;
const RULE_FIELDS = new Set([...Object.values(ACTION_CONFIG_FIELDS).flat().map((f) => f.name), "config"]);

/**
 * The action and field a `400 {code: "invalid_action_config", params: {index, type, field}}`
 * refused, `null` for any other failure. `index` counts the request's `actions` from 0.
 */
export function refusedActionField(e) {
  if (apiErrorCode(e) !== "invalid_action_config") return null;
  const detail = e?.response?.data?.detail;
  const params = detail?.params;
  if (!params || !Number.isInteger(params.index) || typeof params.field !== "string") return null;
  return { index: params.index, field: params.field };
}

/** Whether the server refused the rule's name (`400 {code: "invalid_rule_name", params: {max_length}}`). */
export const refusedRuleName = (e) => apiErrorCode(e) === "invalid_rule_name";

/**
 * The sentence for a failed automation request (rules, dry run, templates), in the operator's
 * language. The refusals the rule routes send as `{code, params}`
 * (super_admin_hq_marketing_routes._rule_error), the dry run's `tenant_not_found` and
 * `rule_trigger_unsupported` get their own wording; anything else (an English server string, an
 * unknown structured detail, a validation list) is the generic `hauto.error`. No server text or
 * raw `detail` reaches the screen. `t` is the `superadmin` translator.
 */
export function automationErrorMessage(e, t) {
  const status = e?.response?.status;
  if (status === 403) return t("hauto.noPermission");
  const code = apiErrorCode(e);
  const detail = e?.response?.data?.detail;
  const params = detail && typeof detail.params === "object" && detail.params ? detail.params : {};
  // Actions are numbered from 1 for people; the server's `index` counts from 0.
  const n = Number.isInteger(params.index) ? params.index + 1 : "";
  if (code === "tenant_not_found") return t("hauto.err.tenantNotFound");
  if (code === "rule_trigger_unsupported") return t("hauto.err.ruleTriggerUnsupported");
  if (code === "invalid_rule_name") return t("hauto.err.ruleName", { max: RULE_NAME_MAX_LENGTH });
  if (code === "invalid_trigger") return t("hauto.err.invalidTrigger");
  if (code === "invalid_action_type") return t("hauto.err.invalidActionType", { n });
  if (code === "rule_too_deep") return t("hauto.err.ruleTooDeep", { max: params.max_depth });
  if (code === "invalid_action_config") {
    const action = actionLabel(t, params.type);
    if (!RULE_FIELDS.has(params.field)) return t("hauto.err.invalidActionConfig.other", { n, action });
    const rule = t(`hauto.cfg.rule.${params.field}`, { max: RULE_MAX[params.field] });
    return t("hauto.err.invalidActionConfig.field", { n, action, rule });
  }
  if (status === 404) return t("hauto.err.ruleNotFound");
  return t("hauto.error");
}

/**
 * What the rule name field says about `problem` (`actionConfigRules.ruleNameProblem`), in the
 * operator's language: nothing visible -> the request for a name, too long -> the count and the
 * limit, anything else (a line break, a control character) -> the rule.
 */
export function ruleNameMessage(problem, t) {
  if (problem.code === "required") return t("hauto.err.nameRequired");
  if (problem.code === "tooLong") return t("hauto.cfg.tooLong", { count: problem.length, max: problem.max });
  return t("hauto.err.ruleName", { max: RULE_NAME_MAX_LENGTH });
}
